import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ai } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../i18n/I18nProvider';
import { AiError, AiOutput, ModelSelect, PAGE_FORMAT } from './shared';

const TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
const MAX_BYTES = 3_750_000;
const TASKS = ['describe', 'text', 'question'] as const;

/** Image to text (14 October 2026): describe a picture, read every word on it, or answer a question about it. */
export function VisionTab() {
  const t = useT();
  const [picture, setPicture] = useState<{ data: string; type: (typeof TYPES)[number]; name: string } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [task, setTask] = useState<(typeof TASKS)[number]>('describe');
  const [question, setQuestion] = useState('');
  const [model, setModel] = useState('');

  const run = useMutation({
    mutationFn: () => ai.vision({
      image: { data: picture!.data, media_type: picture!.type }, task, question: task === 'question' ? question.trim() : null,
      model: model || null, format: PAGE_FORMAT
    })
  });

  function choose(file: File | undefined) {
    setProblem(null);
    if (!file) return;
    if (!TYPES.includes(file.type as (typeof TYPES)[number])) { setProblem(t('ai.vision.wrongType')); return; }
    if (file.size > MAX_BYTES) { setProblem(t('ai.vision.tooBig')); return; }
    const reader = new FileReader();
    reader.onload = () => setPicture({ data: String(reader.result), type: file.type as (typeof TYPES)[number], name: file.name });
    reader.readAsDataURL(file);
  }

  const ready = !!picture && (task !== 'question' || !!question.trim());

  return (
    <Stack gap="lg">
      <Card withBorder>
        <form onSubmit={(e) => { e.preventDefault(); if (ready) run.mutate(); }}>
          <Stack gap="md">
            <Title order={4}>{t('ai.vision.title')}</Title>
            <Stack gap={4}>
              <label className="rr-ai__file">
                <Text role="inputLabel">{t('ai.vision.picture')}</Text>
                <input type="file" accept={TYPES.join(',')} data-testid="ai-vision-file"
                  onChange={(e) => choose(e.currentTarget.files?.[0])} />
              </label>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.vision.pictureHint')}</Text>
            </Stack>
            {problem && <Alert color="warning">{problem}</Alert>}
            {picture && <img className="rr-ai__preview" src={picture.data} alt={picture.name} />}
            <Select label={t('ai.vision.task')} value={task} onChange={(e) => setTask(e.currentTarget.value as (typeof TASKS)[number])}
              data={TASKS.map((x) => ({ value: x, label: t(`ai.vision.task.${x}`) }))} />
            {task === 'question' && (
              <TextInput label={t('ai.vision.question')} description={t('ai.vision.questionHint')} value={question}
                onChange={(e) => setQuestion(e.currentTarget.value)} required />
            )}
            <ModelSelect value={model} onChange={setModel} needsImages />
            <Group justify="flex-end">
              <Button type="submit" loading={run.isPending} disabled={!ready} data-testid="ai-vision-run">{t('ai.vision.run')}</Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.chargeNote')}</Text>
          </Stack>
        </form>
      </Card>
      <AiError error={run.error} />
      {run.data && <Card withBorder><AiOutput result={run.data.data} /></Card>}
    </Stack>
  );
}
